/**
 * watcher status helpers (ADR-044 D2).
 *
 * The backend derives `status` from last_seen_at + degraded_reason with its
 * own 5-minute threshold (pkg/watcherstatus) and every consumer — this
 * console, the offline sweeper, the schedule trigger — reads that one value.
 * The console used to compute its own threshold here (C-26).
 */
import type { WatcherStatus } from '@/types/enums.generated';
import type { WatcherClient } from '@/services/watcher-registry.service';

export const WATCHER_STATUS_LABEL: Record<WatcherStatus, string> = {
  online: '在線',
  degraded: '降級',
  offline: '離線',
};

export const WATCHER_STATUS_COLOR: Record<WatcherStatus, 'success' | 'warning' | 'default'> = {
  online: 'success',
  degraded: 'warning',
  offline: 'default',
};

/** The agent is polling (online or degraded), so it can pick up work. */
export function isAlive(w: Pick<WatcherClient, 'status'>): boolean {
  return w.status === 'online' || w.status === 'degraded';
}

/**
 * Can act on a restore request right now: enabled, alive, and its agent
 * reports a version — agent_version is written only by the commands poll,
 * so its absence means the agent predates ADR-044 and never asks for
 * commands (the backend answers 409 WATCHER_AGENT_OUTDATED).
 */
export function canRestore(w: Pick<WatcherClient, 'status' | 'is_enabled' | 'agent_version'>): boolean {
  return w.is_enabled && isAlive(w) && Boolean(w.agent_version);
}
